import React, { useState, useEffect } from 'react';
import {
  Form, FormGroup, Label, Input, Button,
  Row, Col, Card, CardBody, CardHeader,
  Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import classnames from 'classnames';
import API from '../utils/api';

const SourceForm = ({ source, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'HTTP',
    url: '',
    data_format: 'JSON',
    archive_type: 'NONE',
    auth_type: null,
    auth_config: {},
    fetch_config: {},
    field_mapping: {
      domain: 'domain',
      expiration_date: 'expiration_date',
      registrar: 'registrar',
      price: 'price'
    },
    validation_rules: {
      required_fields: ['domain', 'expiration_date'],
      date_format: '%Y-%m-%d'
    },
    transform_script: '',
    schedule_enabled: false,
    schedule_cron: '',
    schedule_interval_minutes: 60,
    is_active: true,
    epp_categories: []
  });

  const [eppCategories, setEppCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    loadEppCategories();
  }, []);

  useEffect(() => {
    if (source) {
      setFormData(prevFormData => ({
        ...prevFormData,
        ...source,
        epp_categories: source.epp_categories ? source.epp_categories.map(cat => cat.id) : [],
        transform_script: source.transform_script || '',
        field_mapping: source.field_mapping || prevFormData.field_mapping,
        validation_rules: source.validation_rules || prevFormData.validation_rules,
        auth_config: source.auth_config || prevFormData.auth_config,
        fetch_config: source.fetch_config || prevFormData.fetch_config
      }));
    }
  }, [source]);

  const loadEppCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await API.get('/epp/categories');
      setEppCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load EPP categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: value
      }
    });
  };

  const handleEppCategoriesChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    setFormData({
      ...formData,
      epp_categories: selectedOptions
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'basic' })}
            onClick={() => setActiveTab('basic')}
          >
            Basic Configuration
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'auth' })}
            onClick={() => setActiveTab('auth')}
          >
            Authentication
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'mapping' })}
            onClick={() => setActiveTab('mapping')}
          >
            Field Mapping
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === 'advanced' })}
            onClick={() => setActiveTab('advanced')}
          >
            Advanced
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="basic">
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="name" className="text-white">Source Name</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-dark text-white"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="type" className="text-white">Source Type</Label>
                <Input
                  type="select"
                  name="type"
                  id="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="bg-dark text-white"
                >
                  <option value="HTTP">HTTP/HTTPS</option>
                  <option value="FTP">FTP</option>
                  <option value="FTPS">FTPS (Secure FTP)</option>
                  <option value="S3">Amazon S3</option>
                  <option value="API">REST API</option>
                  <option value="FILE">Local File</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="description" className="text-white">Description</Label>
            <Input
              type="textarea"
              name="description"
              id="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="bg-dark text-white"
            />
          </FormGroup>

          <FormGroup>
            <Label for="url" className="text-white">Source URL</Label>
            <Input
              type="text"
              name="url"
              id="url"
              value={formData.url}
              onChange={handleChange}
              placeholder={
                formData.type === 'S3' ? 's3://bucket/path' :
                formData.type === 'FTP' ? 'ftp://ftp.example.com/data/file_{TODAY}.csv' :
                formData.type === 'FTPS' ? 'ftps://ftps.example.com/secure/data_{YESTERDAY}.json' :
                formData.type === 'FILE' ? '/path/to/local/file_{TODAY}.json' :
                'https://example.com/data_{TODAY}.json'
              }
              className="bg-dark text-white"
            />
            <small className="text-muted">
              Supported variables: <code>{`{TODAY}`}</code> and <code>{`{YESTERDAY}`}</code> (format: YYYYmmdd)
            </small>
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="data_format" className="text-white">Data Format</Label>
                <Input
                  type="select"
                  name="data_format"
                  id="data_format"
                  value={formData.data_format}
                  onChange={handleChange}
                  className="bg-dark text-white"
                >
                  <option value="JSON">JSON</option>
                  <option value="CSV">CSV</option>
                  <option value="TSV">TSV</option>
                  <option value="XML">XML</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="archive_type" className="text-white">Archive Type</Label>
                <Input
                  type="select"
                  name="archive_type"
                  id="archive_type"
                  value={formData.archive_type}
                  onChange={handleChange}
                  className="bg-dark text-white"
                >
                  <option value="NONE">None</option>
                  <option value="GZIP">GZIP</option>
                  <option value="ZIP">ZIP</option>
                  <option value="TAR">TAR</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="epp_categories" className="text-white">EPP Categories</Label>
            {loadingCategories ? (
              <div className="text-muted">Loading categories...</div>
            ) : (
              <Input
                type="select"
                name="epp_categories"
                id="epp_categories"
                multiple
                value={formData.epp_categories}
                onChange={handleEppCategoriesChange}
                className="bg-dark text-white"
                style={{ minHeight: '120px' }}
              >
                {eppCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.registrar})
                  </option>
                ))}
              </Input>
            )}
            <small className="text-muted">
              Select EPP categories that can catch domains from this source. Hold Ctrl/Cmd to select multiple.
            </small>
          </FormGroup>

          <FormGroup check>
            <Label check className="text-white">
              <Input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Active
            </Label>
          </FormGroup>
        </TabPane>

        <TabPane tabId="auth">
          <FormGroup>
            <Label for="auth_type" className="text-white">Authentication Type</Label>
            <Input
              type="select"
              name="auth_type"
              id="auth_type"
              value={formData.auth_type || ''}
              onChange={handleChange}
              className="bg-dark text-white"
            >
              <option value="">None</option>
              <option value="basic">Basic Auth</option>
              <option value="bearer">Bearer Token</option>
              <option value="api_key">API Key</option>
              <option value="oauth">OAuth 2.0</option>
            </Input>
          </FormGroup>

          {formData.auth_type === 'basic' && (
            <>
              <FormGroup>
                <Label className="text-white">Username</Label>
                <Input
                  type="text"
                  value={formData.auth_config.username || ''}
                  onChange={(e) => handleNestedChange('auth_config', 'username', e.target.value)}
                  className="bg-dark text-white"
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-white">Password</Label>
                <Input
                  type="password"
                  value={formData.auth_config.password || ''}
                  onChange={(e) => handleNestedChange('auth_config', 'password', e.target.value)}
                  className="bg-dark text-white"
                />
              </FormGroup>
            </>
          )}

          {formData.auth_type === 'bearer' && (
            <FormGroup>
              <Label className="text-white">Bearer Token</Label>
              <Input
                type="text"
                value={formData.auth_config.token || ''}
                onChange={(e) => handleNestedChange('auth_config', 'token', e.target.value)}
                className="bg-dark text-white"
              />
            </FormGroup>
          )}

          {formData.auth_type === 'api_key' && (
            <>
              <FormGroup>
                <Label className="text-white">API Key Header Name</Label>
                <Input
                  type="text"
                  value={formData.auth_config.header || 'X-API-Key'}
                  onChange={(e) => handleNestedChange('auth_config', 'header', e.target.value)}
                  className="bg-dark text-white"
                />
              </FormGroup>
              <FormGroup>
                <Label className="text-white">API Key</Label>
                <Input
                  type="text"
                  value={formData.auth_config.key || ''}
                  onChange={(e) => handleNestedChange('auth_config', 'key', e.target.value)}
                  className="bg-dark text-white"
                />
              </FormGroup>
            </>
          )}
        </TabPane>

        <TabPane tabId="mapping">
          <p className="text-muted">Map source fields to domain fields</p>
          
          <FormGroup>
            <Label className="text-white">Domain Field</Label>
            <Input
              type="text"
              value={formData.field_mapping.domain || ''}
              onChange={(e) => handleNestedChange('field_mapping', 'domain', e.target.value)}
              placeholder="Source field containing domain name"
              className="bg-dark text-white"
            />
          </FormGroup>

          <FormGroup>
            <Label className="text-white">Expiration Date Field</Label>
            <Input
              type="text"
              value={formData.field_mapping.expiration_date || ''}
              onChange={(e) => handleNestedChange('field_mapping', 'expiration_date', e.target.value)}
              placeholder="Source field containing expiration date"
              className="bg-dark text-white"
            />
          </FormGroup>

          <FormGroup>
            <Label className="text-white">Registrar Field</Label>
            <Input
              type="text"
              value={formData.field_mapping.registrar || ''}
              onChange={(e) => handleNestedChange('field_mapping', 'registrar', e.target.value)}
              placeholder="Source field containing registrar"
              className="bg-dark text-white"
            />
          </FormGroup>

          <FormGroup>
            <Label className="text-white">Price Field</Label>
            <Input
              type="text"
              value={formData.field_mapping.price || ''}
              onChange={(e) => handleNestedChange('field_mapping', 'price', e.target.value)}
              placeholder="Source field containing price"
              className="bg-dark text-white"
            />
          </FormGroup>

          <FormGroup>
            <Label className="text-white">Date Format</Label>
            <Input
              type="text"
              value={formData.validation_rules.date_format || '%Y-%m-%d'}
              onChange={(e) => handleNestedChange('validation_rules', 'date_format', e.target.value)}
              placeholder="%Y-%m-%d"
              className="bg-dark text-white"
            />
            <small className="text-muted">Python strftime format</small>
          </FormGroup>
        </TabPane>

        <TabPane tabId="advanced">
          <FormGroup>
            <Label className="text-white">Transform Script</Label>
            <Input
              type="textarea"
              name="transform_script"
              value={formData.transform_script}
              onChange={handleChange}
              rows={5}
              placeholder="Optional Python script to transform data"
              className="bg-dark text-white font-monospace"
            />
            <small className="text-muted">Python code to transform imported data</small>
          </FormGroup>

          <FormGroup>
            <Label className="text-white">Fetch Timeout (seconds)</Label>
            <Input
              type="number"
              value={formData.fetch_config.timeout || 60}
              onChange={(e) => handleNestedChange('fetch_config', 'timeout', parseInt(e.target.value))}
              className="bg-dark text-white"
            />
          </FormGroup>

          {(formData.data_format === 'CSV' || formData.data_format === 'TSV') && (
            <FormGroup>
              <Label className="text-white">CSV/TSV Delimiter</Label>
              <Input
                type="select"
                value={formData.fetch_config.csv_delimiter || (formData.data_format === 'TSV' ? '\t' : ',')}
                onChange={(e) => handleNestedChange('fetch_config', 'csv_delimiter', e.target.value)}
                className="bg-dark text-white"
              >
                <option value=",">Comma (,)</option>
                <option value=";">Semicolon (;)</option>
                <option value="|">Pipe (|)</option>
                <option value="\t">Tab</option>
                <option value=" ">Space</option>
              </Input>
              <small className="text-muted">
                Delimiter used to separate fields in CSV/TSV files
              </small>
            </FormGroup>
          )}

          {formData.type === 'API' && (
            <FormGroup>
              <Label className="text-white">Max Pages</Label>
              <Input
                type="number"
                value={formData.fetch_config.max_pages || 10}
                onChange={(e) => handleNestedChange('fetch_config', 'max_pages', parseInt(e.target.value))}
                className="bg-dark text-white"
              />
            </FormGroup>
          )}
        </TabPane>
      </TabContent>

      <div className="mt-3">
        <Button color="primary" type="submit">Save Source</Button>
        <Button color="secondary" className="ms-2" onClick={onCancel}>Cancel</Button>
      </div>
    </Form>
  );
};

export default SourceForm;